import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, AlertTriangle, CheckCircle2, ClipboardCheck, Clock, RefreshCw, ShieldCheck, Users } from 'lucide-react'
import { apiJson } from '../api'

export interface Assignment {
  id: string
  courseId: string
  courseName: string
  username: string
  employeeName?: string | null
  dueDate: string
  mandatory: boolean
  assignedBy?: string | null
  status: 'assigned' | 'completed' | 'exempted'
  completedAt?: string | null
  recertification: boolean
  createdAt: string
  compliance: 'completed' | 'exempted' | 'overdue' | 'due-soon' | 'on-track'
  daysUntilDue: number
  enrollmentProgress: number | null
}

export const complianceMeta: Record<Assignment['compliance'], { label: string; cls: string }> = {
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
  exempted: { label: 'Exempted', cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
  overdue: { label: 'Overdue', cls: 'bg-red-50 text-red-700 ring-red-100' },
  'due-soon': { label: 'Due soon', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  'on-track': { label: 'On track', cls: 'bg-sky-50 text-sky-700 ring-sky-100' },
}

interface Summary {
  total: number
  completed: number
  overdue: number
  dueSoon: number
  onTrack: number
  exempted: number
  complianceRate: number
  byCourse: Array<{
    courseId: string
    courseName: string
    total: number
    completed: number
    overdue: number
    complianceRate: number
  }>
}

/** Org-wide compliance dashboard (admin/hr) — rendered as a Learning tab. */
export default function Compliance() {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<Summary | null>(null)
  const [error, setError] = useState('')
  const [sweeping, setSweeping] = useState(false)
  const [sweepResult, setSweepResult] = useState('')

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await apiJson<Summary>('/compliance/summary'))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load compliance summary')
    }
  }, [])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const runSweep = async () => {
    setSweeping(true)
    setSweepResult('')
    setError('')
    try {
      const r = await apiJson<{ scanned: number; created: number }>('/compliance/recert-sweep', { method: 'POST', body: '{}' })
      setSweepResult(
        r.created > 0
          ? `Sweep complete — ${r.created} recertification assignment${r.created === 1 ? '' : 's'} created from ${r.scanned} expiring certification${r.scanned === 1 ? '' : 's'}.`
          : `Sweep complete — ${r.scanned} expiring certification${r.scanned === 1 ? '' : 's'} found, all already have active assignments.`,
      )
      await fetchSummary()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sweep failed')
    } finally {
      setSweeping(false)
    }
  }

  if (!summary && !error) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  const cards = summary
    ? [
        { label: 'Assignments', value: summary.total, icon: ClipboardCheck, cls: 'bg-slate-100 text-slate-600' },
        { label: 'Completed', value: summary.completed, icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-600' },
        { label: 'Overdue', value: summary.overdue, icon: AlertTriangle, cls: 'bg-red-50 text-red-600' },
        { label: 'Due soon', value: summary.dueSoon, icon: Clock, cls: 'bg-amber-50 text-amber-600' },
        { label: 'On track', value: summary.onTrack, icon: Users, cls: 'bg-sky-50 text-sky-600' },
      ]
    : []

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}
      {sweepResult && (
        <div className="flex items-center gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <ShieldCheck size={16} className="shrink-0" /> {sweepResult}
        </div>
      )}

      {summary && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {cards.map((c) => (
              <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${c.cls}`}>
                  <c.icon size={16} />
                </div>
                <p className="mt-2.5 text-2xl font-bold tracking-tight">{c.value}</p>
                <p className="text-xs text-slate-400">{c.label}</p>
              </div>
            ))}
            {/* compliance rate */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex h-9 items-center">
                <span className={`text-2xl font-bold tracking-tight ${summary.complianceRate >= 80 ? 'text-emerald-600' : summary.complianceRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {summary.complianceRate}%
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${summary.complianceRate >= 80 ? 'bg-emerald-500' : summary.complianceRate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${summary.complianceRate}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-slate-400">Compliance rate</p>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-bold tracking-tight">Compliance by course</h3>
                <p className="text-xs text-slate-400">Sorted by overdue count — click a course to manage its assignments.</p>
              </div>
              <button
                onClick={runSweep}
                disabled={sweeping}
                className="flex items-center justify-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3.5 py-2 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-50"
              >
                <RefreshCw size={13} className={sweeping ? 'animate-spin' : ''} />
                {sweeping ? 'Running sweep…' : 'Run recertification sweep'}
              </button>
            </div>
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Course</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Assigned</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Completed</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Overdue</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {summary.byCourse.map((c) => (
                  <tr key={c.courseId} className="transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5">
                      <button onClick={() => navigate(`/courses/${c.courseId}/assign`)} className="text-left text-sm font-semibold hover:text-brand-700">
                        {c.courseName}
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{c.total}</td>
                    <td className="px-5 py-3.5 text-sm text-emerald-600">{c.completed}</td>
                    <td className="px-5 py-3.5">
                      {c.overdue > 0 ? (
                        <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-red-100">
                          {c.overdue}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-20 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${c.complianceRate >= 80 ? 'bg-emerald-500' : c.complianceRate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${c.complianceRate}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-slate-600">{c.complianceRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {summary.byCourse.length === 0 && (
              <div className="flex flex-col items-center py-14 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <ClipboardCheck size={22} />
                </div>
                <p className="mt-3 text-sm font-semibold">No assignments yet</p>
                <p className="mt-1 text-xs text-slate-400">Assign a course to employees from its detail page.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
